import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Rule, RuleType } from 'app/modules/common/entities/leave-import/criteria.model';
import { PageLoaderService } from 'app/modules/common/services/page-loader.service';
import { SharedService } from 'app/modules/common/services/shared-service';
import { LeaveImportRulesService } from '../../services/leave-import-rules.service';

export type NewRuleDialogData = {
    criteriaId: number,
};

@Component({
    selector: 'app-new-rule-dialog',
    templateUrl: './new-rule-dialog.component.html',
    styleUrls: ['./new-rule-dialog.component.scss'],
    standalone: false
})
export class NewRuleDialogComponent implements OnInit {

    form: UntypedFormGroup;
    public ruleTypes: RuleType[] = [];


    constructor(
        private fb: UntypedFormBuilder,
        private rulesService: LeaveImportRulesService,
        private sharedService: SharedService,
        private dialogRef: MatDialogRef<NewRuleDialogComponent>,
        private pageLoader: PageLoaderService,
        @Inject(MAT_DIALOG_DATA) private data: NewRuleDialogData,
    ) { }

    ngOnInit(): void {
        this.loadRuleTypes();
        this.initForm();
    }


    /**
     * Initialize form
     */
    private initForm() {

        this.form = this.fb.group({
            criterioId: this.data.criteriaId,
            tipoReglaId: [null, Validators.required],
            parametro1: null,
            param1: [null, Validators.required],
            parametro2: null,
            param2: [null, Validators.required],
            tareaId: [null, Validators.required],
            dias: [null, Validators.max(30)],
        });

        // disable param 1 & 2 until a rule type is selected
        this.form.controls.parametro1.disable();
        this.form.controls.param1.disable();
        this.form.controls.parametro2.disable();
        this.form.controls.param2.disable();

    }


    /**
     * Load rule types into dropdown
     */
    private loadRuleTypes() {
        this.rulesService
            .getRuleTypes()
            .subscribe(res => {

                if (res.code === 0) {
                    this.ruleTypes = res.result;
                } else {
                    this.sharedService.ShowSnackBar("Error en la carga de los tipos de reglas");
                    console.error("Server error: " + res.message);
                }

            }, err => {
                this.sharedService.ShowSnackBar("Error en la carga de los tipos de reglas");
                console.error(err);
            });
    }


    /**
       * Change the parameter controls when rule type changes
       * @param form Rule form where rule type was changed. This forms values will be mutated
       */
    public onRuleTypeChanges() {
        // get rule type ID
        const tipoReglaId: number = this.form.controls.tipoReglaId.value;
        const selectedRuleType = this.ruleTypes.filter(rType => rType.tipoReglaId === tipoReglaId)[0];

        // disable parameters and their values if they are null
        // otherwise enable and set their values
        if (!selectedRuleType?.parametro) {
            this.form.controls.parametro1.disable();
            this.form.controls.param1.disable();
            this.form.controls.parametro1.setValue(null);
        } else {
            this.form.controls.parametro1.enable();
            this.form.controls.param1.enable();
            this.form.controls.parametro1.setValue(selectedRuleType.parametro);
        }

        if (!selectedRuleType?.parametro2) {
            this.form.controls.parametro2.disable();
            this.form.controls.param2.disable();
            this.form.controls.parametro2.setValue(null);
        } else {
            this.form.controls.parametro2.enable();
            this.form.controls.param2.enable();
            this.form.controls.parametro2.setValue(selectedRuleType.parametro2);
        }
    }


    /**
     * Handle create rule event
     */
    async onCreateRule() {

        // validate
        this.form.markAllAsTouched();
        if (!this.form.valid)
            return;

        // create the rule
        const rule: Rule = this.form.value;

        this.pageLoader.ShowPageLoader();
        const ruleCreated = await this.createRule(rule);
        this.pageLoader.HidePageLoader();

        if (ruleCreated === true)
            this.dialogRef.close(true);

    }


    /**
     * Create a rule
     * @param rule Rule data
     * @returns True if the rule is created
     */
    private async createRule(rule: Rule) {

        try {
            const res = await this.rulesService.createRule(rule).toPromise();

            if (res.code === 0) {
                this.sharedService.ShowSnackBar("La regla se ha creado con éxito");
                return true;
            }
            else {
                console.error(res.message);
                this.sharedService.ShowSnackBar("Lo siento, la creación de la regla ha fallado");
            }
        } catch (error) {
            console.error(error);
            this.sharedService.ShowSnackBar("Lo siento, la creación de la regla ha fallado");
        }

        return false;
    }


    /**
     * Close dialog
     */
    close() {
        this.dialogRef.close(false);
    }

}
